import { UseControllerProps, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DeleteIcon } from '@schuettflix/icons-react';
import { SUPPORTED_UNITS } from '@schuettflix/interfaces';
import { FieldsetColumn, FieldsetLabel } from './Fieldset.tsx';
import { cn } from '@/shared/utils/cn.ts';
import { useAutomaticFormContext } from '@/shared/forms/AutomaticProductsForm/context/AutomaticFormContext.tsx';
import { TextFieldInput } from '@/shared/inputs/TextFieldInput.tsx';
import { SelectOrganizationInput, SelectOrganizationInputProps } from '@/shared/inputs/SelectOrganizationInput.tsx';
import { QuantityAndUnitInput } from '@/shared/inputs/QuantityAndUnitInput.tsx';
import { PriceInput } from '@/shared/inputs/PriceInput.tsx';
import { TaxClassInput } from '@/shared/inputs/TaxClassInput.tsx';
import { PurchasePriceHints } from '@/shared/components/PurchasePriceHints.tsx';
import { SalesPriceHints } from '@/shared/components/SalesPriceHints.tsx';
import { TaxClassOptions } from '@/clients/tax-classes/useTaxClasses.ts';
import { AutomaticProductsFormValues } from '../schema/getAutomaticProductsFormSchema.ts';
import { ProductTypeInput, ProductTypeInputProps } from '@/shared/inputs/ProductTypeInput.tsx';
import { PRODUCT_NAME_LABEL, DEFAULT_PRODUCT_NAME_LABELS } from '@/phrase/ProductNameLabels.ts';

export type AdditionalServiceIndexPath<TField extends string> = `additionalServices.${number}.${TField}`;

type AdditionalServiceRowProps = {
    productTypeProps: ProductTypeInputProps;
    productNameProps: Pick<
        UseControllerProps<AutomaticProductsFormValues, AdditionalServiceIndexPath<'productName'>>,
        'name' | 'defaultValue'
    >;
    currencyCodeProps: Pick<
        UseControllerProps<AutomaticProductsFormValues, AdditionalServiceIndexPath<'currencyCode'>>,
        'name'
    >;
    partnerSelectionProps: Pick<
        UseControllerProps<AutomaticProductsFormValues, AdditionalServiceIndexPath<'partnerOrganizationId'>>,
        'name' | 'defaultValue'
    > & { exclude?: SelectOrganizationInputProps['exclude']; defaultValueLabel?: string };
    salesQuantityProps: Pick<
        UseControllerProps<AutomaticProductsFormValues, AdditionalServiceIndexPath<'salesQuantity'>>,
        'name' | 'defaultValue'
    > & { testId?: string };
    salesUnitProps: Pick<
        UseControllerProps<AutomaticProductsFormValues, AdditionalServiceIndexPath<'salesUnit'>>,
        'name' | 'defaultValue' | 'disabled'
    > & { supportedUnitNames?: SUPPORTED_UNITS[]; onUnitChange?: (unit?: SUPPORTED_UNITS) => void };
    salesPriceProps: Pick<
        UseControllerProps<AutomaticProductsFormValues, AdditionalServiceIndexPath<'salesPrice'>>,
        'name' | 'defaultValue'
    > & { testId?: string };
    salesTaxClassIdProps: Pick<
        UseControllerProps<AutomaticProductsFormValues, AdditionalServiceIndexPath<'salesTaxClassId'>>,
        'name' | 'defaultValue'
    > & { options: TaxClassOptions[]; testId?: string };
    purchaseQuantityProps: Pick<
        UseControllerProps<AutomaticProductsFormValues, AdditionalServiceIndexPath<'purchaseQuantity'>>,
        'name' | 'defaultValue'
    > & { testId?: string };
    purchaseUnitProps: Pick<
        UseControllerProps<AutomaticProductsFormValues, AdditionalServiceIndexPath<'purchaseUnit'>>,
        'name' | 'defaultValue'
    > & { supportedUnitNames?: SUPPORTED_UNITS[]; onUnitChange?: (unit?: SUPPORTED_UNITS) => void };
    purchasePriceProps: Pick<
        UseControllerProps<AutomaticProductsFormValues, AdditionalServiceIndexPath<'purchasePrice'>>,
        'name' | 'defaultValue'
    > & { testId?: string };
    purchaseTaxClassIdProps: Pick<
        UseControllerProps<AutomaticProductsFormValues, AdditionalServiceIndexPath<'purchaseTaxClassId'>>,
        'name' | 'defaultValue'
    > & { options: TaxClassOptions[]; testId?: string };
    showZeroPurchasePriceProps: Pick<
        UseControllerProps<AutomaticProductsFormValues, AdditionalServiceIndexPath<'showZeroPurchasePrice'>>,
        'name' | 'defaultValue'
    >;
    deleteDisabled?: boolean;
    showZeroSalesPriceProps: Pick<
        UseControllerProps<AutomaticProductsFormValues, AdditionalServiceIndexPath<'showZeroSalesPrice'>>,
        'name' | 'defaultValue'
    >;
    dataTest?: string;
    onDelete?: () => void;
};

export function AdditionalServiceRow({
    productTypeProps,
    productNameProps,
    currencyCodeProps,
    partnerSelectionProps,
    salesQuantityProps,
    salesUnitProps,
    salesPriceProps,
    salesTaxClassIdProps,
    purchaseQuantityProps,
    purchaseUnitProps,
    purchasePriceProps,
    purchaseTaxClassIdProps,
    showZeroPurchasePriceProps,
    showZeroSalesPriceProps,
    dataTest,
    onDelete,
    deleteDisabled,
}: AdditionalServiceRowProps) {
    const { t } = useTranslation();
    const { watch, setValue, getValues } = useFormContext<AutomaticProductsFormValues>();
    const { customerOrganization } = useAutomaticFormContext();

    const [
        currencyCode,
        salesUnit,
        purchaseUnit,
        partnerOrganizationId,
        salesPrice,
        purchasePrice,
        showZeroSalesPrice,
        showZeroPurchasePrice,
    ] = watch([
        currencyCodeProps.name,
        salesUnitProps.name,
        purchaseUnitProps.name,
        partnerSelectionProps.name,
        salesPriceProps.name,
        purchasePriceProps.name,
        showZeroSalesPriceProps.name,
        showZeroPurchasePriceProps.name,
    ]);

    return (
        <div className="flex flex-col gap-2" data-test={dataTest}>
            <div className="flex items-start gap-2">
                <div className="@sm:grid-cols-2 @2xl:grid-cols-8 grid min-w-0 flex-1 grid-cols-1 gap-2">
                    <FieldsetColumn
                        className="@2xl:col-span-2 col-span-full"
                        label={t('forms.automaticProductsForm.labels.productType')}
                    >
                        <ProductTypeInput
                            {...productTypeProps}
                            onProductTypeChange={productType => {
                                const productName = getValues(productNameProps.name);
                                const canOverwriteProductName =
                                    !productName || DEFAULT_PRODUCT_NAME_LABELS.some(label => t(label) === productName);

                                if (productType && canOverwriteProductName) {
                                    setValue(productNameProps.name, t(PRODUCT_NAME_LABEL[productType]));
                                }
                            }}
                            dataTest="service-product-type"
                        />
                    </FieldsetColumn>
                    <FieldsetColumn
                        className="@2xl:col-span-2 col-span-full"
                        label={t('forms.automaticProductsForm.labels.serviceDescription')}
                    >
                        <TextFieldInput
                            {...productNameProps}
                            placeHolder={t('product.category.custom')}
                            size="lg"
                            dataTest="service-description"
                        />
                    </FieldsetColumn>

                    <FieldsetColumn className="@2xl:col-span-2 col-span-full">
                        <div className="grid grid-cols-2 gap-1">
                            <FieldsetLabel>{t('forms.automaticProductsForm.labels.salesQuantity')}</FieldsetLabel>
                            <FieldsetLabel>{t('forms.automaticProductsForm.labels.salesUnit')}</FieldsetLabel>
                        </div>
                        <QuantityAndUnitInput
                            quantityProps={{ ...salesQuantityProps, testId: 'sales-quantity-input', disabled: true }}
                            unitProps={salesUnitProps}
                        />
                    </FieldsetColumn>

                    <FieldsetColumn className="@2xl:col-span-2 col-span-full">
                        <div className="grid grid-cols-2 gap-1">
                            <FieldsetLabel className="col-span-1">
                                {t('forms.automaticProductsForm.labels.salesUnitPrice')}
                            </FieldsetLabel>
                            <FieldsetLabel className="col-span-1">
                                {t('forms.automaticProductsForm.labels.salesTaxClass')}
                            </FieldsetLabel>
                        </div>
                        <div className="grid grid-cols-2 items-start gap-2">
                            <PriceInput
                                {...salesPriceProps}
                                unit={salesUnit}
                                currencyCode={currencyCode}
                                testId="sales-price"
                                className="col-span-1"
                            />
                            <TaxClassInput {...salesTaxClassIdProps} testId="sales-tax-class" className="col-span-1" />
                        </div>
                    </FieldsetColumn>

                    <FieldsetColumn
                        className="@2xl:col-span-4 col-span-full"
                        label={t('product.addPosition.partner.label')}
                    >
                        <SelectOrganizationInput
                            {...partnerSelectionProps}
                            placeholder={t('product.addPosition.partner.label')}
                            dataTest="partner-organization"
                        />
                    </FieldsetColumn>

                    <FieldsetColumn className="@2xl:col-span-2 col-span-full">
                        <div className="grid grid-cols-2 gap-1">
                            <FieldsetLabel>{t('forms.automaticProductsForm.labels.purchaseQuantity')}</FieldsetLabel>
                            <FieldsetLabel>{t('forms.automaticProductsForm.labels.purchaseUnit')}</FieldsetLabel>
                        </div>
                        <QuantityAndUnitInput
                            quantityProps={{
                                ...purchaseQuantityProps,
                                testId: 'purchase-quantity-input',
                                disabled: true,
                            }}
                            unitProps={purchaseUnitProps}
                        />
                    </FieldsetColumn>

                    <FieldsetColumn className="@2xl:col-span-2 col-span-full">
                        <div className="grid grid-cols-2 gap-1">
                            <FieldsetLabel className="col-span-1">
                                {t('forms.automaticProductsForm.labels.purchaseUnitPrice')}
                            </FieldsetLabel>
                            <FieldsetLabel className="col-span-1">
                                {t('forms.automaticProductsForm.labels.purchaseTaxClass')}
                            </FieldsetLabel>
                        </div>
                        <div className="grid grid-cols-2 items-start gap-2">
                            <PriceInput
                                {...purchasePriceProps}
                                unit={purchaseUnit}
                                currencyCode={currencyCode}
                                testId="purchase-price"
                                className="col-span-1"
                            />
                            <TaxClassInput
                                {...purchaseTaxClassIdProps}
                                testId="purchase-tax-class"
                                className="col-span-1"
                            />
                        </div>
                    </FieldsetColumn>
                </div>

                <FieldsetColumn className="shrink-0 items-center px-2 pt-6">
                    <DeleteIcon
                        monochrome
                        disabled={deleteDisabled}
                        className={cn({
                            'cursor-not-allowed': deleteDisabled,
                            'hover:cursor-pointer': !deleteDisabled,
                        })}
                        onClick={deleteDisabled ? undefined : onDelete}
                        data-test="delete-additional-service"
                    />
                </FieldsetColumn>
            </div>

            <div className="flex flex-col gap-2">
                <PurchasePriceHints
                    partnerOrganizationId={partnerOrganizationId}
                    purchasePrice={purchasePrice}
                    showZeroPurchasePrice={showZeroPurchasePrice ?? false}
                    onZeroPurchasePriceChange={() => setValue(showZeroPurchasePriceProps.name, !showZeroPurchasePrice)}
                />
                <SalesPriceHints
                    customerOrganizationId={customerOrganization.id}
                    salesPrice={salesPrice}
                    showZeroSalesPrice={showZeroSalesPrice ?? false}
                    onZeroSalesPriceChange={() => setValue(showZeroSalesPriceProps.name, !showZeroSalesPrice)}
                />
            </div>
        </div>
    );
}
